'use client';

import Cookie from 'js-cookie';
import { useEffect } from 'react';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import type { CommonCurrencyInfo } from '@/utils/currency/util-currency';
import useSyncToken from './useSyncToken';

/**
 * 客户端同步 token、拉取账户（替代 layout 中 getUserState + prepareStoreInitialData 用户字段）
 */
export const useClientDataInit = (currencyInfo: CommonCurrencyInfo) => {
  const token = useStore((state) => state.token);
  const tokenReady = useStore((state) => state.clientTokenReady);
  const refreshAccountInfo = useStore((state) => state.refreshAccountInfo);
  const setWishNum = useStore((state) => state.setWishNum);
  const setUserInfoReady = useStore((state) => state.setUserInfoReady);
  useSyncToken();

  useEffect(() => {
    if (!tokenReady) return;
    let active = true;
    if (!token) {
      setUserInfoReady(true);
      return;
    }
    // 统一账户入口按 Store 的 token + 登录版本拒绝旧响应，不能依赖 App Cookie。
    void refreshAccountInfo({ initializeUserEffects: true }).then((user) => {
      if (!active) return;
      if (typeof user?.wish_num === 'number') setWishNum(user.wish_num);
      // 资料请求失败会返回 null，也必须结束初始化等待；购物车可用当前 token 计价并隔离缓存。
      // 此标记只解除等待，不把失败当成登录成功，也不补写缺失的账户资料。
      setUserInfoReady(true);
    });
    return () => {
      active = false;
    };
  }, [token, tokenReady, refreshAccountInfo, setUserInfoReady, setWishNum]);

  // Store 已在 SSR 时初始化；这里只同步存储，不重新请求或覆盖币种状态。
  useEffect(() => {
    const { currency, source } = currencyInfo;
    if (!currency?.name) return;

    if (source === 'URL' || source === 'IP') {
      Cookie.set('zl-currency', currency.name, { expires: 365, sameSite: 'lax', path: '/' });
    }

    try {
      if (window.self === window.top) {
        localStorage.setItem('zl-currency', JSON.stringify(currency));
      }
    } catch (error) {
      logger.error('Failed to persist server currency:', error);
    }
  }, [currencyInfo]);
};
